import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IRequestState, IErrorDetails } from './interfaces';
import { fetchDocTypesByCustomer } from './docTypesSlice';
import Api from '../api/helpers';
import {
  GetAvailableSysPrinsRequest,
  ICustomerDto,
  IDocTypeDto,
  IGetAvailableSysPrinsResponse,
  IGetCustomersResponse,
  ISysPrinDto,
  IGetVantageQueryForCustomerResponse,
  IGetVantageQueryForCustomerRequest,
  GetVantageQueryForCustomerRequest,
  CustomerVantageQueryDto,
  ICustomerVantageQueryDto,
} from '../api/client';

export interface ICustomersState {
  customers: { customers: ICustomerDto[] } & IRequestState;
  activeCustomer: ICustomerDto | null;
  sysPrinsByDocType: { sysPrinsByDocType: ISysPrinDto[] } & IRequestState;
  copySysPrinsByDocType: { list: ISysPrinDto[] } & IRequestState;
  selectedSysPrin: { selectedSysPrin: ICustomerDto[] } & IRequestState;
  vantageQueries: { list: CustomerVantageQueryDto[] } & IRequestState;
  showVantageQueriesBySysPrinDropDown: boolean;
  previousResultWithoutPagination: ICustomerVantageQueryDto[];
  appliedFisrtLevelSysPrins: boolean;
}

const initialState: ICustomersState = {
  customers: {
    customers: [],
    isRequestActive: false,
    errorDetails: { statusCode: null, userMessage: null },
  },
  activeCustomer: null,
  sysPrinsByDocType: {
    sysPrinsByDocType: [],
    isRequestActive: false,
    errorDetails: { statusCode: null, userMessage: null },
  },
  selectedSysPrin: {
    selectedSysPrin: [],
    isRequestActive: false,
    errorDetails: { statusCode: null, userMessage: null },
  },
  copySysPrinsByDocType: {
    list: [],
    isRequestActive: false,
    errorDetails: { statusCode: null, userMessage: null },
  },
  vantageQueries: {
    list: [],
    isRequestActive: false,
    errorDetails: { statusCode: null, userMessage: null },
  },
  // vantageCorpEntity: {
  //   list: [],
  //   isRequestActive: false,
  //   errorDetails: { statusCode: null, userMessage: null },
  // },
  showVantageQueriesBySysPrinDropDown: true,
  previousResultWithoutPagination: [],
  appliedFisrtLevelSysPrins: false,
};

const customersSlice = createSlice({
  name: 'customers',
  initialState,
  reducers: {
    setSysPrinsByDocType: (state, action: PayloadAction<ISysPrinDto[]>) => {
      state.sysPrinsByDocType.sysPrinsByDocType = action.payload;
    },
    setCopySysPrinsByDocType: (state, action: PayloadAction<ISysPrinDto[]>) => {
      state.copySysPrinsByDocType.list = action.payload;
    },
    setVantageQueriesBySysPrinDropDown: (state, action: PayloadAction<boolean>) => {
      state.showVantageQueriesBySysPrinDropDown = action.payload;
    },
    setPreviousResultWithoutPagination: (state, action: PayloadAction<ICustomerVantageQueryDto[]>) => {
      state.previousResultWithoutPagination = action.payload;
    },
    setAppliedFisrtLevelSysPrins: (state, action: PayloadAction<boolean>) => {
      state.appliedFisrtLevelSysPrins = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchCustomers.pending, (state) => {
      state.customers.isRequestActive = true;
    });
    builder.addCase(fetchCustomers.fulfilled, (state, action) => {
      state.customers.isRequestActive = false;
      state.customers.customers = action.payload.customers;
    });
    builder.addCase(setActiveCustomer.fulfilled, (state, action) => {
      state.activeCustomer = action.payload;
    });
    builder.addCase(fetchSysPrinsByDocType.pending, (state) => {
      state.sysPrinsByDocType.isRequestActive = true;
    });
    builder.addCase(fetchSysPrinsByDocType.fulfilled, (state, action) => {
      state.sysPrinsByDocType.isRequestActive = false;
      state.sysPrinsByDocType.sysPrinsByDocType = action.payload.sysprins;
    });
    builder.addCase(fetchCopySysPrinsByDocType.pending, (state) => {
      state.copySysPrinsByDocType.isRequestActive = true;
    });
    builder.addCase(fetchCopySysPrinsByDocType.fulfilled, (state, action) => {
      state.copySysPrinsByDocType.isRequestActive = false;
      state.copySysPrinsByDocType.list = action.payload.sysprins;
    });
    builder.addCase(fetchVantageQueries.pending, (state) => {
      state.vantageQueries.isRequestActive = true;
    });
    builder.addCase(fetchVantageQueries.fulfilled, (state) => {
      state.vantageQueries.isRequestActive = false;
    });
    builder.addCase(fetchVantageQueries.rejected, (state) => {
      state.vantageQueries.isRequestActive = false;
    });
  },
});

export const fetchCustomers = createAsyncThunk<
  IGetCustomersResponse,
  { sysPrinInUrl: string | undefined; docTypeIdInUrl: string | undefined } | undefined
>('customers/fetchCustomers', async (request, thunkApi) => {
  const client = Api.getCustomersClient(thunkApi.dispatch);
  const response = await client.getCustomers();

  if (request?.sysPrinInUrl && request?.docTypeIdInUrl) {
    const customer = response.customers.find((c) => c.sysPrin.sysPrin === request.sysPrinInUrl);
    if (customer) {
      thunkApi.dispatch(setActiveCustomer({ customer, docTypeIdInUrl: request.docTypeIdInUrl }));
      return response;
    }
  }

  thunkApi.dispatch(setActiveCustomer({ customer: response.customers[0], docTypeIdInUrl: undefined }));
  return response;
});

export const setActiveCustomer = createAsyncThunk<
  ICustomerDto,
  { customer: ICustomerDto; docTypeIdInUrl: string | undefined }
>('customers/setActiveCustomer', async (request, thunkApi) => {
  thunkApi.dispatch(fetchDocTypesByCustomer(request));
  return request.customer;
});

// disabling no explicit any because reduxjs/toolkit GetApiThunk is not being exported
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const fetchSysPrinsThunk = async (docType: IDocTypeDto, thunkApi: any) => {
  let response: IGetAvailableSysPrinsResponse = { sysprins: [] };
  if (docType.primaryKey) {
    const client = Api.getCustomersClient(thunkApi.dispatch);
    response = await client.getAvailableSysPrins(
      new GetAvailableSysPrinsRequest({ docTypePrimaryKey: docType.primaryKey }),
    );
  }
  return response;
};

export const fetchSysPrinsByDocType = createAsyncThunk<IGetAvailableSysPrinsResponse, IDocTypeDto>(
  'customers/availableSysPrins',
  async (docType, thunkApi) => {
    return fetchSysPrinsThunk(docType, thunkApi);
  },
);

export const fetchCopySysPrinsByDocType = createAsyncThunk<IGetAvailableSysPrinsResponse, IDocTypeDto>(
  'customers/availableCopySysPrins',
  async (docType, thunkApi) => {
    return fetchSysPrinsThunk(docType, thunkApi);
  },
);

export const fetchVantageQueries = createAsyncThunk<
  IGetVantageQueryForCustomerResponse,
  IGetVantageQueryForCustomerRequest,
  { rejectValue: IErrorDetails }
>('selectivity/fetchVantageQueries', async (request, thunkApi) => {
  const client = Api.getCustomersClient(thunkApi.dispatch);
  const response = await client.getVantageQueriesForCustomer(new GetVantageQueryForCustomerRequest(request));
  return response;
});

export const {
  setSysPrinsByDocType,
  setCopySysPrinsByDocType,
  setVantageQueriesBySysPrinDropDown,
  setPreviousResultWithoutPagination,
  setAppliedFisrtLevelSysPrins,
} = customersSlice.actions;
export default customersSlice.reducer;
